import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

/**
 * Splits text holding email addresses into the pieces between which a line may break: after each `@`,
 * and after each dot in the part before it. `ryk.nicholls@supportingeducation.com` gives
 * `['ryk.', 'nicholls@', 'supportingeducation.com']`.
 *
 * The domain is kept whole on purpose. Line breaking is greedy, so a break chance after
 * `supportingeducation.` would be taken whenever it fits, leaving `com` alone on the next line, which
 * is the defect this exists to fix. Text with no `@` comes back as one piece.
 */
export function emailBreakParts(value: string | null | undefined): string[] {
  const text = value ?? '';
  const parts: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '@' || (ch === '.' && localPartContinues(text, i + 1))) {
      parts.push(text.slice(start, i + 1));
      start = i + 1;
    }
  }
  if (start < text.length || parts.length === 0) {
    parts.push(text.slice(start));
  }
  return parts;
}

/** True when an `@` follows before the address ends (whitespace, comma or semicolon). */
function localPartContinues(text: string, from: number): boolean {
  for (let i = from; i < text.length; i++) {
    const ch = text[i];
    if (ch === '@') { return true; }
    if (ch === ',' || ch === ';' || /\s/.test(ch)) { return false; }
  }
  return false;
}

/**
 * Shows text holding email addresses (a "changed by", a recipient list) so that it wraps after the `@`
 * rather than at any character. Admin table cells set overflow-wrap: anywhere (admin-page.css), which
 * is what split `…supportingeducation.co` / `m`; this element opts out of that with break-word, so an
 * address breaks only at the <wbr> chances it is given, or mid-word only when one piece is still too
 * wide for the line.
 */
@Component({
  selector: 'admin-email',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `@for (part of parts; track $index) {@if (!$first) {<wbr>}{{part}}}`,
  styles: [':host { overflow-wrap: break-word; }'],
})
export class AdminEmailComponent {
  parts: string[] = [];

  @Input() set value(value: string | null | undefined) {
    this.parts = emailBreakParts(value);
  }
}
